// Task 7.5: Analytics with GDPR Consent Gating

import posthog from 'posthog-js';
import { captureError, captureMessage, buildTags, toSerializable } from '@/lib/monitoring/logger';

type GtagArgs = [string, ...unknown[]];

interface ConsentState {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  recordings?: boolean;
}

interface AnalyticsEvent {
  event: string;
  props?: Record<string, unknown>;
}

const UTM_PROPERTY_NAMES = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
] as const;

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
// Use reverse proxy by default to bypass ad blockers; fall back to direct host if configured
const POSTHOG_API_HOST = '/ingest';
const POSTHOG_UI_HOST = 'https://eu.posthog.com';
type PostHogInitOptions = NonNullable<Parameters<typeof posthog.init>[1]>;
const POSTHOG_DEFAULTS = '2026-01-30' as unknown as PostHogInitOptions['defaults'];

// Internal domain for filtering internal users from analytics
const INTERNAL_EMAIL_DOMAIN = '@scopax.com';

let posthogInitialized = false;
let sessionRecordingStarted = false;

function readConsentState(): ConsentState | null {
  if (typeof window === 'undefined') return null;

  try {
    const consentCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('gdpr-consent='));

    if (!consentCookie) return null;

    const consentValue = consentCookie.split('=').slice(1).join('=');
    return JSON.parse(decodeURIComponent(consentValue)) as ConsentState;
  } catch (error) {
    void captureError(error, {
      tags: buildTags('analytics', 'consent-read'),
      extra: toSerializable({})
    });
    return null;
  }
}

function isSessionReplayAllowedRoute(pathname: string): boolean {
  const excludedPrefixes = ['/auth', '/protected', '/api', '/docs'];
  if (excludedPrefixes.some(prefix => pathname === prefix || pathname.startsWith(`${prefix}/`))) {
    return false;
  }

  return pathname !== '/privacy' && pathname !== '/terms';
}

// Check if consent has been granted for analytics
export function hasAnalyticsConsent(): boolean {
  if (typeof window === 'undefined') return false;

  const consent = readConsentState();
  return consent?.analytics === true;
}

export function hasSessionReplayConsent(): boolean {
  if (typeof window === 'undefined') return false;

  const consent = readConsentState();
  return consent?.analytics === true && consent.recordings === true;
}

export function syncPosthogSessionRecording() {
  if (typeof window === 'undefined') return;
  if (!posthogInitialized) return;

  const hasRecordingConsent = hasSessionReplayConsent();
  const routeAllowed = isSessionReplayAllowedRoute(window.location.pathname);

  try {
    if (!hasRecordingConsent || !routeAllowed) {
      posthog.stopSessionRecording();
      sessionRecordingStarted = false;
      return;
    }

    if (!sessionRecordingStarted) {
      posthog.startSessionRecording();
      sessionRecordingStarted = true;
    }
  } catch (error) {
    void captureError(error, {
      tags: buildTags('analytics', 'posthog-session-recording'),
      extra: toSerializable({ routeAllowed, hasRecordingConsent })
    });
  }
}

// Initialize PostHog (events themselves remain consent-gated via track())
export function initializePosthog() {
  if (typeof window === 'undefined') return;
  if (process.env.NODE_ENV === 'test') return;
  if (!POSTHOG_KEY) return;
  if (posthogInitialized) return;
  if (!hasAnalyticsConsent()) return;

  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_API_HOST,
      ui_host: POSTHOG_UI_HOST,
      defaults: POSTHOG_DEFAULTS,
      person_profiles: 'identified_only',
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      disable_session_recording: true,
      disable_external_dependency_loading: !hasSessionReplayConsent(),
      advanced_disable_feature_flags: true, // Disable feature flags fetching for privacy
    });
    // Expose for debugging and manual verification in browser console
    if (typeof window !== 'undefined') {
      (window as unknown as { posthog?: typeof posthog }).posthog = posthog;
    }
    posthogInitialized = true;
    syncPosthogSessionRecording();
  } catch (error) {
    void captureError(error, {
      tags: buildTags('analytics', 'posthog-init'),
      extra: toSerializable({})
    });
  }
}

/**
 * User Identification
 * Call after successful authentication to enable cross-session user tracking.
 * Respects GDPR consent - only identifies if analytics consent granted.
 */
export interface UserIdentityProps {
  email?: string;
  role?: 'owner' | 'admin' | 'member' | 'viewer';
  tenant_id?: string;
  tenant_name?: string;
}

export function identifyUser(userId: string, properties?: UserIdentityProps) {
  if (typeof window === 'undefined') return;
  if (!posthogInitialized) return;
  if (!hasAnalyticsConsent()) return;

  try {
    const isInternal = properties?.email?.endsWith(INTERNAL_EMAIL_DOMAIN) ?? false;
    
    posthog.identify(userId, {
      ...properties,
      is_internal: isInternal,
    });

    captureMessage('analytics.user.identified', 'info', {
      tags: buildTags('analytics', 'identify'),
      extra: toSerializable({ userId, isInternal })
    });
  } catch (error) {
    void captureError(error, {
      tags: buildTags('analytics', 'posthog-identify'),
      extra: toSerializable({ userId })
    });
  }
}

/**
 * Reset user identity on logout.
 * Generates a new anonymous ID for the device.
 */
export function resetUser() {
  if (typeof window === 'undefined') return;
  if (!posthogInitialized) return;

  try {
    posthog.reset();
  } catch (error) {
    void captureError(error, {
      tags: buildTags('analytics', 'posthog-reset'),
      extra: toSerializable({})
    });
  }
}

/**
 * Group Analytics for Tenant/Company tracking.
 * Enables B2B analytics: churn analysis, feature adoption by company, etc.
 */
export interface TenantGroupProps {
  name?: string;
  plan?: string;
  instrument_count?: number;
}

export function setTenantGroup(tenantId: string, properties?: TenantGroupProps) {
  if (typeof window === 'undefined') return;
  if (!posthogInitialized) return;
  if (!hasAnalyticsConsent()) return;

  try {
    posthog.group('company', tenantId, properties);

    captureMessage('analytics.group.set', 'info', {
      tags: buildTags('analytics', 'group'),
      extra: toSerializable({ tenantId })
    });
  } catch (error) {
    void captureError(error, {
      tags: buildTags('analytics', 'posthog-group'),
      extra: toSerializable({ tenantId })
    });
  }
}

// Initialize Google Analytics with Consent Mode v2
export function initializeAnalytics(consentState?: ConsentState) {
  if (typeof window === 'undefined') return;
  
  const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
  if (!measurementId) return;
  
  // Initialize gtag if not already present
  if (!window.gtag) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = (...args: GtagArgs) => {
      window.dataLayer.push(args);
    };
  }
  
  // Set default consent state (denied)
  window.gtag('consent', 'default', {
    'analytics_storage': 'denied',
    'ad_storage': 'denied',
    'functionality_storage': 'granted',
    'security_storage': 'granted',
    'region': ['EU'], // EU region settings
  });
  
  // Update consent if provided
  if (consentState) {
    window.gtag('consent', 'update', {
      'analytics_storage': consentState.analytics ? 'granted' : 'denied',
      'ad_storage': consentState.marketing ? 'granted' : 'denied',
    });
  }
  
  // Configure Google Analytics
  window.gtag('config', measurementId, {
    anonymize_ip: true, // IP anonymization for privacy
    allow_google_signals: false, // Disable advertising features
    allow_ad_personalization_signals: false,
    region: 'EU',
  });
  
  // Load GA4 script
  if (!document.querySelector(`script[src*="${measurementId}"]`)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);
  }
}

// Track events with consent checking
export function track(event: AnalyticsEvent) {
  if (typeof window === 'undefined') return;
  
  // Only track if consent granted
  if (!hasAnalyticsConsent()) {
    captureMessage('analytics.event.blocked', 'info', {
      tags: buildTags('analytics', 'consent-blocked'),
      extra: toSerializable({ event: event.event })
    });
    return;
  }
  
  if (window.gtag) {
    window.gtag('event', event.event, event.props || {});
  }

  if (posthogInitialized) {
    try {
      if (event.event === 'page_view') {
        posthog.capture('$pageview', {
          ...(event.props || {}),
          $current_url: typeof window !== 'undefined' ? window.location.href : undefined,
        });
      }
      posthog.capture(event.event, event.props || {});
    } catch (error) {
      void captureError(error, {
        tags: buildTags('analytics', 'posthog-capture'),
        extra: toSerializable({ event: event.event })
      });
    }
  }
}

export function trackCurrentPageView() {
  if (typeof window === 'undefined') return;

  const { pathname, search, hash, href } = window.location;
  const page = `${pathname}${search}${hash}`;
  const searchParams = new URLSearchParams(search);
  const acquisitionProps: Record<string, string> = {};

  for (const propertyName of UTM_PROPERTY_NAMES) {
    const value = searchParams.get(propertyName);
    if (value) {
      acquisitionProps[propertyName] = value;
    }
  }

  if (document.referrer) {
    acquisitionProps.referrer = document.referrer;
    acquisitionProps.$referrer = document.referrer;

    try {
      const referringDomain = new URL(document.referrer).hostname;
      if (referringDomain) {
        acquisitionProps.referring_domain = referringDomain;
        acquisitionProps.$referring_domain = referringDomain;
      }
    } catch {
      // Keep the raw referrer even when the browser reports a non-URL value.
    }
  }

  track({
    event: 'page_view',
    props: {
      page,
      path: pathname,
      url: href,
      ...acquisitionProps,
    },
  });
}

// Predefined event tracking functions
export const analytics = {
  // Page view tracking
  pageView: (page: string) => {
    track({
      event: 'page_view',
      props: { page }
    });
  },
  
  // CTA click tracking
  ctaClick: (cta: string, variant?: string, extras?: Record<string, unknown>) => {
    track({
      event: 'cta_click',
      props: { cta, variant, ...(extras ?? {}) }
    });
  },
  
  // Demo interaction tracking
  demoInteraction: (type: string) => {
    track({
      event: 'demo_interaction', 
      props: { type }
    });
  },
  
  // ROI calculation tracking
  roiCalculated: (instruments: number, hoursSaved: number) => {
    track({
      event: 'roi_calculated',
      props: { instruments, hours_saved: hoursSaved }
    });
  },
  
  // Signup funnel tracking
  signupStarted: (source: string) => {
    track({
      event: 'signup_started',
      props: { source }
    });
  },
  
  signupCompleted: (plan: string) => {
    track({
      event: 'signup_completed',
      props: { plan }
    });
  },
  
  // Consent tracking
  consentGranted: (categories: string[]) => {
    track({
      event: 'consent_granted',
      props: { categories }
    });
  },
};

// Update consent and reinitialize analytics
export function updateConsent(consentState: ConsentState) {
  if (typeof window === 'undefined') return;
  
  // Store consent in cookie
  const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000); // 1 year
  const secureAttribute = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `gdpr-consent=${encodeURIComponent(JSON.stringify(consentState))}; expires=${expires.toUTCString()}; SameSite=Lax${secureAttribute}; path=/`;
  
  initializeAnalytics(consentState);
  if (consentState.analytics) {
    initializePosthog();
  }

  if (posthogInitialized) {
    try {
      posthog.set_config({
        disable_external_dependency_loading: !hasSessionReplayConsent(),
      });
    } catch (error) {
      void captureError(error, {
        tags: buildTags('analytics', 'posthog-config'),
        extra: toSerializable({})
      });
    }
    syncPosthogSessionRecording();
  }

  // Update Google Analytics consent
  if (window.gtag) {
    window.gtag('consent', 'update', {
      'analytics_storage': consentState.analytics ? 'granted' : 'denied',
      'ad_storage': consentState.marketing ? 'granted' : 'denied',
    });
  }

  if (consentState.analytics) {
    const categories: string[] = [];
    if (consentState.analytics) categories.push('analytics');
    if (consentState.recordings) categories.push('recordings');
    if (consentState.marketing) categories.push('marketing');
    
    analytics.consentGranted(categories);
  }

  window.dispatchEvent(new CustomEvent('consent-updated', { detail: consentState }));
}

// Initialize on page load
if (typeof window !== 'undefined') {
  // Check for existing consent
  const hasConsent = hasAnalyticsConsent();
  
  if (hasConsent) {
    try {
      const consentCookie = document.cookie
        .split('; ')
        .find(row => row.startsWith('gdpr-consent='));
      
      if (consentCookie) {
        const consentValue = consentCookie.split('=')[1];
        const consentState: ConsentState = JSON.parse(decodeURIComponent(consentValue));
        initializeAnalytics(consentState);
      }
    } catch (error) {
      void captureError(error, {
        tags: buildTags('analytics', 'initialize'),
        extra: toSerializable({})
      });
    }
  } else {
    // Initialize with denied consent
    initializeAnalytics();
  }
}
